<template>
  <div>
    <es-loader v-if="loading" style="margin: 6.5rem"/>
    <es-exception v-if="!loading && loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-if="!loading && !loadingError">
      <section class="section control-block">
        <div class="control">
          <div class="select">
            <select v-model="selectedOrg">
              <option
                :value="org.code"
                v-for="(org, index) in organizations"
                :key="index"
              >{{ org.abbreviation }}</option>
            </select>
          </div>
        </div>
      </section>
      <div v-if="this.histories.totalCount !== 0">
        <div class="match-header">
          <p>{{ orgName }} ( {{ histories.totalCount }} )</p>
        </div>
        <div class="document" v-for="(child, index) in histories.data" :key="index">
          <div class="result-block">
            <div class="columns is-desktop columns-header">
              <div class="column column-left is-one-third">
                <div class="flex-container-left">
                  <div>
                    <span class="is-primary is-bold">
                      <router-link
                        :to="{ name: 'locationDetails', params: {id: child.stateLocationId} }"
                        class="font-weight-bold"
                      >{{ child.name }}</router-link>
                    </span>
                    <span class="has-text-grey is-italic"> {{ child.stateLocationId }} </span>
                  </div>
                  <div>
                    {{ child.street1}}
                    <span>{{ child.street2 }}</span>
                  </div>
                  <div>
                    {{ child.city }}
                    <span> {{ child.stateCode }}</span>
                    <span> {{ child.zip }}</span>
                  </div>
                </div>
              </div>
              <div class="column is-one-quarter"
                :class="{'column-middle': child.localCode !== undefined}">
                <div v-if="child.localCode !== undefined" class="flex-container-middle">
                  <div>
                    <div>
                      {{ orgName }}:
                      <span class="is-black">{{ child.localCode }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="column column-middle">
                <div class="flex-container-middle">
                  <table>
                    <tr>
                      <td class="has-text-right">Source:</td>
                       <td>
                         <span class="is-black">
                          {{ child.source.description }}
                         </span>
                       </td>
                    </tr>
                    <tr>
                      <td class="has-text-right">Updated:</td>
                      <td>
                        <span class="is-black">
                          {{ child.lastUpdateDate | formatDateMMDDYYYY }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="has-text-right">Updated By:</td>
                      <td>
                        <span class="is-black">
                          {{ child.lastUpdateByUser.firstName }}
                          {{ child.lastUpdateByUser.lastName }}
                        </span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
              <div v-if="toggled" class="column column-right is-2">
                <div class="flex-container-right">
                  <a @click.prevent="toggled = false; child.expand = !child.expand; toggled = true;">
                    <font-awesome-icon :icon="['fas', 'caret-down']" size="lg" v-if="!child.expand" />
                    <font-awesome-icon :icon="['fas', 'caret-up']" size="lg" v-if="child.expand" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div v-if="child.expand" class="document-container">
            <div class="document document-body">
              <div class="content">
                <es-form>
                  <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon :icon="['fas', 'map-marker-alt']" size="lg" />
                      </div>
                      <div class="header-elements">
                        <h6 class="title">Location Details</h6>
                      </div>
                    </div>
                  </template>
                  <template #es-form>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Name</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.name"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">State Location ID</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.stateLocationId"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Source</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.source.description"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="selectedOrg !== 'Master'" class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Local Code</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.localCode"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Address 1</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.street1"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Address 2</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.street2"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">City</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.city"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">State</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.state.description"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Zip</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.zip"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </es-form>
                <es-form>
                  <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon :icon="['fas', 'address-book']" size="lg"/>
                      </div>
                      <div class="header-elements">
                        <h6 class="title">Contact</h6>
                      </div>
                    </div>
                  </template>
                  <template #es-form>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">First Name</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.adminFirstName"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Last Name</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.adminLastName"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Title</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.title.description"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Email</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              v-model="child.adminEmail"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Phone</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              :value="maskPhone(child.adminPhone)"
                              readonly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </es-form>

                <es-form>
                  <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon :icon="['fas', 'map-marked-alt']" size="lg" />
                      </div>
                      <div class="header-elements">
                        <h6 class="title">More</h6>
                      </div>
                    </div>
                  </template>
                  <template #es-form>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Start Date</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              readonly
                              :value="child.startDateYYYYMMDD | formatDateMMDDYYYY"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">End Date</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input is-static"
                              type="text"
                              readonly
                              :value="child.endDateYYYYMMDD | formatDateMMDDYYYY"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Last Updated</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              type="text"
                              class="input is-static"
                              readonly
                              :value="child.lastUpdateDateOrig | formatDateMMDDYYYY"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Last Updated By</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              type="text"
                              class="input is-static"
                              readonly
                              :value="child.lastUpdateByOrig"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Created</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              type="text"
                              class="input is-static"
                              readonly
                              :value="child.createdDateOrig | formatDateMMDDYYYY"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">Created By</label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              type="text"
                              class="input is-static"
                              readonly
                              :value="child.createdByOrig"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </es-form>
              </div>
            </div>
          </div>
        </div>
        <div>
          <a v-if="this.pageSize < this.histories.totalCount" @click="loadMore()">Load More</a>
        </div>
      </div>
      <div v-else>
        <div class="no-result-container">
          <div class="icon">
            <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="4x"/>
          </div>
          <p></p>
          <h4>No results were found.</h4>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretDown,
  faCaretUp,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import PhoneUtils from '@/mixins/PhoneUtils';
import NotificationManager from '@/mixins/NotificationManager';
import EsLoader from '@/components/EsLoader';

library.add(
  faCaretDown,
  faCaretUp,
  faExclamationTriangle,
);

export default {
  mixins: [PhoneUtils, NotificationManager],
  components: {
    EsException,
    EsForm,
    EsLoader,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      organizations: [],
      selectedOrg: 'Master',
      histories: {},
      locName: '',
      orgName: '',
      toggled: true,
      pageSize: 25,
      sortField: 'lastUpdateDate',
      sortOrder: 'DESC',
      messages: [],
      loading: null,
      loadingError: null,
    };
  },
  watch: {
    selectedOrg(val) {
      this.getHistoryRecords(val);
    },
    pageSize(val) {
      if (val > 1) {
        this.getHistoryRecords(this.selectedOrg);
      }
    },
  },
  mounted() {
    this.onLoad();
  },
  computed: {
    ...mapGetters('lookups', [
      'activeStates',
      'activeTitles',
    ]),
  },
  methods: {
    ...mapActions([
      'getLocationOrganization',
      'getLocationHistory',
      'getLocationOrganizationHistory',
    ]),
    ...mapActions('lookups', [
      'loadStates',
      'loadTitles',
    ]),
    async getHistoryRecords(code) {
      let json = null;
      try {
        const params = new URLSearchParams();
        params.append('pageSize', this.pageSize);
        const sorters = {};
        sorters.field = this.sortField;
        sorters.order = this.sortOrder;
        const sort = [];
        sort.push(sorters);
        if (sorters) params.append('sort', JSON.stringify(sort));
        if (code === 'Master') {
          const response = await this.getLocationHistory({
            id: this.id,
            params,
          });
          json = response.data;
        } else {
          const response = await this.getLocationOrganizationHistory({
            id: this.id,
            code,
            params,
          });
          json = response.data;
        }
      } catch (error) {
        this.apiMsg = `${error.response.status === 500 ? 'Network Error' : error.response.data.message}`;
        this.messages.push(this.apiMsg);
        this.hasError = true;
        this.showMessage();
      }
      if (json != null) this.initialize(json);
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try{
        await Promise.all([
          this.loadStates(),
          this.loadTitles(),
        ]);
        const methodGetLocationOrganization = await this.getLocationOrganization(this.id);
        this.organizations = methodGetLocationOrganization.data.map(item => item.organization);
        this.organizations.push({ code: 'Master', abbreviation: 'Master' });
      } catch (error) {
        this.apiMsg = `${error.response.status === 500 ? 'Network Error' : error.response.data.message}`;
        this.messages.push(this.apiMsg);
        this.loadingError = true;
        this.hasError = true;
        this.showMessage();
      }
      this.getHistoryRecords('Master');
      this.loading = false;
    },

    initialize(data) {
      const org = this.organizations.find(l => l.abbreviation === this.selectedOrg);
      this.orgName = org.abbreviation;
      if (data !== '') {
        this.histories = data;
        const d = this.histories.data.map((h) => {
          const h1 = h;
          h1.state = this.getLookupFromCode(this.activeStates, h1.stateCode);
          h1.title = this.getLookupFromCode(this.activeTitles, h1.adminTitle);
          if (h1.startDate !== undefined && h1.startDate !== '') {
            h1.startDateYYYYMMDD = moment(h1.startDate).format('YYYY-MM-DD');
          } else {
            h1.startDate = '';
            h1.startDateYYYYMMDD = '';
          }
          if (h1.endDate !== undefined && h1.endDate !== '') {
            h1.endDateYYYYMMDD = moment(h1.endDate).format('YYYY-MM-DD');
          } else {
            h1.endDate = '';
            h1.endDateYYYYMMDD = '';
          }
          h1.createdBy = `${h1.createdByUser.firstName} ${h1.createdByUser.lastName}`;
          h1.lastUpdateBy = `${h1.lastUpdateByUser.firstName} ${h1.lastUpdateByUser.lastName}`;
          h1.createdByOrig = `${h1.createdByUserOrig.firstName} ${h1.createdByUserOrig.lastName}`;
          h1.lastUpdateByOrig = `${h1.lastUpdateByUserOrig.firstName} ${h1.lastUpdateByUserOrig.lastName}`;
          h1.expand = false;
          return h1;
        });
        this.histories.data = d;
      } else {
        this.histories = { totalCount: 0 };
      }
    },
    showMessage() {
      const type = this.hasError ? 'danger' : 'success';
      this.showNotification(type, this.messages);
    },
    getLookupFromCode(list, code) {
      let o = { code: '', description: '' };
      if (code !== undefined && code !== '') {
        const found = list.filter(s => s.code === code);
        if (found !== undefined) {
          o = { code: found[0].code, description: found[0].description };
        }
      }
      return o;
    },
    loadMore() {
      if (this.pageSize < this.histories.totalCount) {
        this.pageSize += this.pageSize;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .document
    max-width: 1168px
    margin-bottom: 30px
  .document-body
    margin-left: 10%
    margin-right: 10%
  .es-header-box
    margin-top: auto
    margin-bottom: auto
  .is-black
    color: #000
  .match-header
    padding-top: 1rem
    padding-bottom: 1rem
    margin-left: auto
    margin-right: auto
    max-width: 1168px
    color: #6c757d
    text-align: left
    font-weight: 700
    font-size: .85rem
    margin-top: -20px
  .control-block
    padding-left: 1rem
    padding-top: 1rem
    padding-bottom: 2rem
  .result-block
    height: 90px
  .columns
    height: 100%
  .columns-header
    padding-top: 1rem
    padding-bottom: 1rem
  .column-left
    text-align: left
    color: #888
    padding-left: 2rem
    padding-top: 0
    padding-bottom: 0
    height: 100%
    display: flex
    margin-top: auto
    margin-bottom: auto
  .column-middle
    text-align: middle
    color: #888
    padding-left: 2rem
    padding-top: 0
    padding-bottom: 0
    height: 100%
    display: flex
    justify-content: center
    margin-top: auto
    margin-bottom: auto
    border-left: 1px solid rgb(222, 226, 230)
  .has-text-right
      padding: 0 8px 4px 0
  .column-right
    text-align: left
    color: #888
    padding-left: 2rem
    padding-top: 0
    padding-bottom: 0
    height: 100%
    display: flex
    justify-content: right
    margin-top: auto
    margin-bottom: auto
  .flex-container-left
    display: flex
    justify-content: center
    flex-direction: column
  .flex-container-middle
    display: flex
    justify-content: center
    align-items: center
    flex-direction: column
  .flex-container-percent
    display: flex
    justify-content: center
    align-items: center
    flex-direction: row
  .flex-container-right
    display: flex
    justify-content: flex-end
    padding-right: 1rem
    align-items: center
    width: 100%
  .no-result-container
    padding: 6.5rem
</style>
